import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { CircleAlert, CircleCheck, Newspaper, RotateCw, Sparkles } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useAuth } from '../../../contexts/AuthContext';
import { intelligenceReportService, ReportLanguage } from '../../../services/intelligenceReportService';
import type { NewsFeed as NewsFeedData } from '../../../types/news';
import { NewsFeed } from '../../../components/intelligence/NewsFeed';
import { GenerationSteps, GenerationStatus } from '../../../components/intelligence/GenerationSteps';
import {
  Button,
  Card,
  Dialog,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  SectionHeader,
  SegmentedControl,
  Skeleton,
  Spinner,
  Textarea,
} from '../../../components/ui';

interface StakeholderNewsTabProps {
  entityId: string;
  entityName: string;
}

const STEPS = [
  'Reading the record and signals on file',
  'Connecting to the research provider',
  'Searching press, official record and social',
  'Verifying links',
  'Merging and saving',
  'Complete',
];

type WindowMonths = 6 | 12 | 24 | 0;

const WINDOW_ITEMS: Array<{ value: WindowMonths; label: string; title: string }> = [
  { value: 6, label: '6m', title: 'The last six months' },
  { value: 12, label: '12m', title: 'The last twelve months' },
  { value: 24, label: '24m', title: 'The last two years' },
  { value: 0, label: 'All', title: 'Everything findable, recent coverage first' },
];

const windowLabel = (months: number) => (months > 0 ? `${months} months` : 'all time');

const formatUpdated = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const formatBytes = (chars: number) => (chars < 1024 ? `${chars} B` : `${(chars / 1024).toFixed(1)} KB`);

export const StakeholderNewsTab: React.FC<StakeholderNewsTabProps> = ({ entityId, entityName }) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [stepMessage, setStepMessage] = useState<string | null>(null);
  const [received, setReceived] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [focus, setFocus] = useState('');
  const [language, setLanguage] = useState<ReportLanguage>('en');
  const [windowMonths, setWindowMonths] = useState<WindowMonths>(12);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState<NewsFeedData | null>(null);
  const [legacyHtml, setLegacyHtml] = useState('');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  // Load what is on file
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    intelligenceReportService
      .getNews(entityId)
      .then((data) => {
        if (cancelled || !data) return;
        setLastUpdated(data.updatedAt);
        if (data.feed) {
          setFeed(data.feed);
          setLegacyHtml('');
        } else {
          setFeed(null);
          setLegacyHtml(data.content || '');
        }
      })
      .catch(() => {
        // No news yet
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  const sanitizedLegacy = useMemo(() => (legacyHtml ? DOMPurify.sanitize(legacyHtml) : ''), [legacyHtml]);

  const handleGenerate = useCallback(async () => {
    setStatus('generating');
    setCurrentStep(0);
    setStepMessage(null);
    setReceived(0);
    setErrorMessage(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const generator = intelligenceReportService.generateNews(entityId, controller.signal, {
        focus: focus.trim() || undefined,
        language,
        windowMonths,
      });

      let chars = 0;
      for await (const event of generator) {
        if (controller.signal.aborted) break;

        switch (event.type) {
          case 'progress':
            setCurrentStep(event.step);
            setStepMessage(event.message);
            break;
          case 'content':
            chars += event.text.length;
            setReceived(chars);
            break;
          case 'complete':
            setCurrentStep(STEPS.length);
            setStatus('complete');
            if (event.feed) {
              setFeed(event.feed);
              setLegacyHtml('');
              setLastUpdated(event.feed.generatedAt);
            }
            break;
          case 'error':
            setErrorMessage(event.message || 'An unknown error occurred');
            setStatus('error');
            break;
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setStatus('idle');
        setCurrentStep(0);
      } else {
        setErrorMessage(error.message || 'Failed to generate news');
        setStatus('error');
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, [entityId, focus, language, windowMonths]);

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setStatus('idle');
    setCurrentStep(0);
    setStepMessage(null);
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    if (status === 'generating') handleCancel();
    setIsModalOpen(false);
    setTimeout(() => {
      setStatus('idle');
      setCurrentStep(0);
      setStepMessage(null);
      setErrorMessage(null);
      setFocus('');
    }, 300);
  }, [status, handleCancel]);

  const completedStats = feed?.stats;

  return (
    <div className="space-y-6">
      <style>{legacyStyles}</style>

      {/* Verified, structured coverage. The watch and the signals it has
          collected live in the Monitoring tab, as they do for organizations. */}
      <section>
        <SectionHeader
          title="Coverage"
          aside={
            isAdmin && (feed || legacyHtml) ? (
              <Button size="xs" variant="secondary" onClick={() => setIsModalOpen(true)}>
                <RotateCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                Refresh
              </Button>
            ) : undefined
          }
        />
        {loading ? (
          <Card divided aria-busy="true" aria-label="Loading news">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2 px-4 py-3">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </Card>
        ) : feed ? (
          <>
            <p
              className="mb-3 text-[12px] text-neutral-400"
              title={lastUpdated ? new Date(lastUpdated).toLocaleString() : undefined}
            >
              {lastUpdated && `Updated ${formatUpdated(lastUpdated)}`}
              {feed.provider && ` · ${feed.provider}${feed.model ? ` · ${feed.model}` : ''}`}
              {` · ${windowLabel(feed.windowMonths)}`}
            </p>
            <NewsFeed feed={feed} />
          </>
        ) : legacyHtml ? (
          <>
            <p className="mb-3 text-[12px] text-neutral-400">
              {lastUpdated && `Updated ${formatUpdated(lastUpdated)}`} · older format, refresh to verify links and sources
            </p>
            <div className="news-feed" dangerouslySetInnerHTML={{ __html: sanitizedLegacy }} />
          </>
        ) : (
          <Card>
            <EmptyState
              icon={Newspaper}
              title="No coverage yet"
              action={
                isAdmin ? (
                  <Button size="sm" variant="primary" onClick={() => setIsModalOpen(true)}>
                    <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                    Search
                  </Button>
                ) : undefined
              }
            />
          </Card>
        )}
      </section>

      {/* Generation sheet */}
      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        labelledBy="news-generation-title"
        handle
        className="max-h-[92vh] sm:max-h-[86vh] sm:max-w-lg"
      >
        <DialogHeader
          id="news-generation-title"
          eyebrow={<span className="block max-w-[260px] truncate sm:max-w-[480px]">{entityName}</span>}
          title="Search coverage"
          onClose={handleClose}
        />

        {status === 'idle' && (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-5 pt-4">
            <Field label="Focus" htmlFor="news-context" hint="Optional. Topics or questions to concentrate on.">
              <Textarea
                id="news-context"
                rows={3}
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                className="resize-none"
                placeholder="e.g. Renewable energy announcements"
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Search back" htmlFor="news-window" hint="Recent coverage is completed first.">
                <SegmentedControl<string>
                  label="Window"
                  value={String(windowMonths)}
                  onChange={(value) => setWindowMonths(Number(value) as WindowMonths)}
                  items={WINDOW_ITEMS.map((item) => ({
                    value: String(item.value),
                    label: item.label,
                    title: item.title,
                  }))}
                />
              </Field>
              <Field label="Summaries in" htmlFor="news-language">
                <SegmentedControl<ReportLanguage>
                  label="Language"
                  value={language}
                  onChange={setLanguage}
                  items={[
                    { value: 'en', label: 'English' },
                    { value: 'fr', label: 'Français' },
                  ]}
                />
              </Field>
            </div>
            <p className="text-[12px] leading-relaxed text-neutral-400">
              Searches local, national, regional and international press, the official record, and the
              person&apos;s social accounts. Every link is checked before the results are saved.
            </p>
          </div>
        )}

        {status !== 'idle' && (
          <>
            <GenerationSteps labels={STEPS} currentStep={currentStep} status={status} message={stepMessage} />

            {status === 'error' && errorMessage && (
              <p
                role="alert"
                className="mx-5 mb-3 flex flex-shrink-0 items-start gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700"
              >
                <CircleAlert className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                <span className="min-w-0 break-words">{errorMessage}</span>
              </p>
            )}

            {status === 'complete' && completedStats && (
              <div className="mx-5 mb-4 rounded-2xl border border-neutral-200 px-4 py-3 text-[13px] text-neutral-600">
                <p className="flex items-center gap-2 text-ink">
                  <CircleCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} />
                  {feed!.items.length} item{feed!.items.length === 1 ? '' : 's'} on file
                </p>
                <p className="mt-1 pl-6 text-[12px] text-neutral-400">
                  {[
                    `${completedStats.found} found`,
                    completedStats.rejected ? `${completedStats.rejected} rejected` : null,
                    completedStats.droppedDead ? `${completedStats.droppedDead} dead link${completedStats.droppedDead === 1 ? '' : 's'} dropped` : null,
                    completedStats.fromMonitoring ? `${completedStats.fromMonitoring} from monitoring` : null,
                    completedStats.sources ? `${completedStats.sources} outlet${completedStats.sources === 1 ? '' : 's'}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          {status === 'idle' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleGenerate}>
                Search
              </Button>
            </>
          )}

          {status === 'generating' && (
            <>
              <span className="mr-auto inline-flex items-center gap-2 text-[12px] tabular-nums text-neutral-400">
                <Spinner className="h-3 w-3" />
                {received ? formatBytes(received) : 'Searching'}
              </span>
              <Button variant="secondary" onClick={handleCancel}>
                Stop
              </Button>
            </>
          )}

          {status === 'complete' && (
            <Button variant="primary" onClick={handleClose}>
              View
            </Button>
          )}

          {status === 'error' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button variant="primary" onClick={handleGenerate}>
                <RotateCw className="h-4 w-4" strokeWidth={1.75} />
                Retry
              </Button>
            </>
          )}
        </DialogFooter>
      </Dialog>
    </div>
  );
};

/* ── Older feeds ──────────────────────────────────────────────────────────
 * Records written before the feed became structured hold model-written HTML
 * (news-overview, news-item…). They are shown as one hairline list until the
 * analyst refreshes them.
 */
const legacyStyles = `
.news-feed { max-width: 100%; font-size: 13px; color: #0a0a0a; overflow-wrap: anywhere; }
.news-feed .news-overview { margin: 0 0 16px; }
.news-feed .news-overview h2 { display: none; }
.news-feed .news-overview .news-summary { margin: 0; font-size: 14px; line-height: 1.6; color: #404040; }
.news-feed .news-overview .news-count { margin: 6px 0 0; font-size: 12px; color: #a3a3a3; }
.news-feed .news-item { display: flex; margin: 0; padding: 11px 16px 12px; background: #fff; border: 1px solid #e5e5e5; border-top-width: 0; }
.news-feed .news-item:first-of-type { border-top-width: 1px; border-top-left-radius: 16px; border-top-right-radius: 16px; }
.news-feed .news-item:last-of-type { border-bottom-left-radius: 16px; border-bottom-right-radius: 16px; }
.news-feed .news-image, .news-feed .news-placeholder, .news-feed .news-footer { display: none; }
.news-feed .news-content { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.news-feed .news-title { order: -1; margin: 0; font-size: 14px; font-weight: 400; line-height: 1.4; }
.news-feed .news-title a { color: inherit; text-decoration: none; }
.news-feed .news-title a:hover { text-decoration: underline; text-decoration-color: #d4d4d4; text-underline-offset: 2px; }
.news-feed .news-meta { display: flex; flex-wrap: wrap; align-items: center; margin-top: 2px; font-size: 12px; color: #a3a3a3; }
.news-feed .news-source { color: #737373; }
.news-feed .news-source + .news-date::before { content: '\\00b7'; margin: 0 6px; color: #d4d4d4; }
.news-feed .news-excerpt { margin: 6px 0 0; font-size: 13px; line-height: 1.55; color: #737373; }
.news-feed .news-sources { margin-top: 16px; padding: 0 4px; }
.news-feed .news-sources h3 { margin: 0 0 2px; font-size: 12px; font-weight: 400; color: #a3a3a3; }
.news-feed .news-sources p { margin: 0; font-size: 12px; line-height: 1.5; color: #a3a3a3; }
`;
